import { Controller } from "@hotwired/stimulus";
import { getFileSizeForHuman } from "../../shared/getFileSizeForHuman";
import { readFileAsDataURL } from "../../shared/readFileAsDataURL";
import { isFileMimeTypeAccepted } from "../../shared/acceptedFileType";

const FILE_MAX_SIZE = 1024 * 1024 * 10; // 10MB

export default class extends Controller {
  static targets = [
    "uploadButton",
    "uploadImage",
    "spinner",
    "textarea",
    "messageSendButton",
    "inputFile",
    "confirmModal",
    "fileListTemplate",
    "fileList",
    "fileItem",
    "fileImage",
    "fileName",
    "fileSize",
    "form",
  ];

  /**
   * ファイルを選択する
   */
  selectFiles() {
    // ファイル選択を初期化する
    this.inputFileTarget.value = "";
    this.inputFileTarget.disabled = false;

    this.inputFileTarget.click();
  }

  /**
   * ファイル送信確認用のモーダルを表示
   */
  showConfirmModal() {
    // 選択されたファイルが何も無い場合は終了
    if (this.inputFileTarget.files.length == 0) {
      return;
    }

    for (const file of this.inputFileTarget.files) {
      // アップロードできない形式ファイルの場合は中断する
      if (!this.#isAcceptedFileType(file)) {
        alert(
          "送信できるファイルは画像ファイル(PNG,GIF,JPEG)かPDFファイルのみです。",
        );
        return;
      }

      // ファイルサイズが大きい場合は中断する
      // 実際には10MBまで許可しているが、ユーザーには5MBまでと表示する
      if (file.size > FILE_MAX_SIZE) {
        alert(
          "ファイルサイズが大きすぎます。5MB以内のファイルを添付してください。",
        );
        return;
      }
    }

    // 選択したファイルでプレビューを作成する
    this.#createPreview();

    // 確認用のモーダルを表示
    window.BootstrapWrapper.showModal(this.confirmModalTarget);
  }

  /**
   * ファイルを送信する
   */
  sendFiles() {
    // ファイルが指定されていない場合は中断
    if (this.inputFileTarget.files.length == 0) {
      return;
    }

    // ファイル選択ボタンを無効化して送信中にする
    this.uploadButtonTarget.disabled = true;
    this.uploadImageTarget.classList.add("d-none");
    this.spinnerTarget.classList.add("spinner-border");

    // テキストエリアとメッセージ送信ボタンを無効化する
    this.textareaTarget.disabled = true;
    this.messageSendButtonTarget.disabled = true;

    const formData = new FormData(this.formTarget);

    fetch(this.formTarget.action, {
      method: "POST",
      body: formData,
      redirect: "error",
    })
      .then((response) => {
        if (!response.ok) {
          // 422 (ルームがアクティブでない場合) など。
          console.error("ファイル送信エラー (" + response.status + ")");
          return Promise.reject();
        }

        // 無効化していたものを戻す
        this.messageSendButtonTarget.disabled = false;
        this.textareaTarget.disabled = false;
        this.spinnerTarget.classList.remove("spinner-border");
        this.uploadImageTarget.classList.remove("d-none");
        this.uploadButtonTarget.disabled = false;
      })
      .catch((error) => {
        if (error) {
          console.error(error);
        }
        // 422 (ルームがアクティブでない場合) やリダイレクト（ログアウト状態）やネットワークエラーなど。
        // もしかするとメッセージ作成できている場合もあるかもしれないので「送信に失敗しました」ではなくて汎用的なエラーメッセージを表示
        alert("エラーが発生しました");
        location.reload();
      });
  }

  /**
   * プレビューを作成する
   */
  #createPreview() {
    // プレビューを初期化する
    this.fileItemTargets.forEach((element) => {
      element.remove();
    });

    // 選択したファイルでプレビュー用の要素を作る
    const files = this.inputFileTarget.files;
    for (let i = 0; i < files.length; i++) {
      const previewItem = document.importNode(
        this.fileListTemplateTarget.content,
        true,
      );
      this.fileListTarget.appendChild(previewItem);
    }

    // ファイルプレビューとファイル名を更新する
    for (let i = 0; i < this.fileItemTargets.length; i++) {
      const file = files[i];

      // 画像の場合はプレビューを更新
      // 更新されない場合はPDFアイコンが出る
      if (file.type.includes("image/")) {
        this.#setPreviewImage(file, this.fileImageTargets[i]);
      }

      // ファイル名
      this.fileNameTargets[i].textContent = file.name;

      // ファイルサイズ
      this.fileSizeTargets[i].textContent = getFileSizeForHuman(file.size);
    }
  }

  async #setPreviewImage(file, target) {
    const url = await readFileAsDataURL(file);
    target.src = url;
  }

  /**
   * アップロード可能な拡張子かチェックする
   * @param {File} file
   * @returns {Boolean}
   */
  #isAcceptedFileType(file) {
    const acceptedMimeTypes = this.inputFileTarget.accept
      .split(",")
      .map((type) => type.trim());

    // 今のところ `accept` にはMIMEタイプしか指定されていないので、拡張子は無視して良い。
    return isFileMimeTypeAccepted(file, acceptedMimeTypes);
  }
}
